'use client';

import { PriceData, Product } from '@/models/Product';
import { useEffect, useState } from 'react';
import OptimizedProductCard from './OptimizedProductCard';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from './ui/carousel';

export default function ProductListSwiper({
  products,
  priceData,
  slug,
  title,
}: {
  products?: Product[];
  priceData: PriceData | null;
  slug: string;
  title: string;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) return;

    const updateIndex = () => {
      setCurrent(api.selectedScrollSnap() + 1);
    };

    setCount(api.scrollSnapList().length);
    updateIndex();
    api.on('select', updateIndex);

    return () => {
      api?.off('select', updateIndex);
    };
  }, [api]);

  const groupedProducts =
    products?.reduce(
      (acc, product, index) => {
        if (index % 2 === 0) {
          acc.push({
            first: product,
            second: products[index + 1] || null,
          });
        }
        return acc;
      },
      [] as { first: Product; second: Product | null }[],
    ) || [];

  return (
    <Carousel
      className="w-full px-4 md:px-0"
      setApi={setApi}
      opts={{ align: 'start' }}
      aria-label={`${title} ürünleri kaydırmalı liste`}
    >
      <CarouselContent className="pt-4 md:pt-8">
        {groupedProducts.map((pair, index) => (
          <CarouselItem
            key={index}
            className="basis-1/2 md:basis-1/3 lg:basis-1/4 xl:basis-1/5 2xl:basis-1/6"
          >
            <div className="flex flex-col gap-2 pr-2">
              <OptimizedProductCard
                key={pair.first.id}
                product={pair.first}
                priority={index < 2}
                price={priceData?.[pair.first.id]}
                listId={slug}
                listName={title}
              />
              {pair.second && (
                <OptimizedProductCard
                  key={pair.second.id}
                  product={pair.second}
                  priority={index < 2}
                  price={priceData?.[pair.second.id]}
                  listId={slug}
                  listName={title}
                />
              )}
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>

      {current > 1 && (
        <CarouselPrevious className="absolute left-4 size-12" aria-label="Önceki ürünleri göster" />
      )}
      {current < count && (
        <CarouselNext className="absolute right-4 size-12" aria-label="Sonraki ürünleri göster" />
      )}
    </Carousel>
  );
}
